"use client"

import type React from "react"
import { observer } from "mobx-react-lite"
import { useEffect, useMemo, useState, useRef } from "react"
import { toPng } from "html-to-image"
import {
  Download,
  Calendar,
  BarChart3,
  FileText,
  TrendingUp,
  RefreshCw,
  Mail,
  Users,
  Target,
  CheckCircle2,
  XCircle,
  Building2,
} from "lucide-react"
import { useAppContext } from "../../../shared/functions/Context"
import type Objective from "../../../shared/models/Objective"
import type Measure from "../../../shared/models/Measure"
import type User from "../../../shared/models/User"
import { LoadingEllipsis } from "../../../shared/components/loading/Loading"
import BarGraph from "../../../shared/components/graph-components/BarGraph"
import { totalQ2MeasureRating, totalQ4MeasureRating } from "../../shared/functions/Scorecard"
import ErrorBoundary from "../../../shared/components/error-boundary/ErrorBoundary"
import { cn } from "../../shared/utils/utils"
import { Button } from "../company-dashboard/ui/button"
import { Badge } from "../company-dashboard/ui/badge"
import { Card, CardContent, CardHeader, CardTitle} from "../company-dashboard/ui/card"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../company-dashboard/ui/dropdown-menu"


interface TabsProps {
  tab: "Q2" | "Q4"
  setTab: React.Dispatch<React.SetStateAction<"Q2" | "Q4">>
  handleExportPDF: () => Promise<void>
  handleExportExcel: () => Promise<void>
  handleExportDetailedReport: () => Promise<void>
  handleSendEmail: () => Promise<void>
  loading: boolean
  departmentName: string
}

const DepartmentTabs = observer(
  ({
    tab,
    setTab,
    handleExportPDF,
    handleExportExcel,
    handleExportDetailedReport,
    handleSendEmail,
    loading,
    departmentName,
  }: TabsProps) => {
    const { store } = useAppContext()
    const [refreshing, setRefreshing] = useState(false)

    const handleRefresh = async () => {
      setRefreshing(true)
      // Simulate refresh - in real app, this would reload data
      setTimeout(() => setRefreshing(false), 1000)
    }

    return (
      <Card className="mb-6 shadow-lg border-0 bg-gradient-to-r from-blue-50 to-indigo-50">
        <CardContent className="pt-6 px-6">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 lg:gap-6">
            <div className="flex items-center space-x-4 flex-wrap gap-2">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-600 rounded-lg">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Department Dashboard</h1>
                  <p className="text-sm text-gray-600">Departmental performance management and analytics</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Badge variant="outline" className="text-xs bg-white">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  Live Analytics
                </Badge>
                <Badge variant="outline" className="text-xs bg-white">
                  <Users className="w-3 h-3 mr-1" />
                  {store.user.departmentalUsers.length} People
                </Badge>
              </div>
            </div>

            <div className="flex items-center space-x-3 flex-wrap gap-2">
              {/* Refresh Button */}
              <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} className="bg-white">
                <RefreshCw className={cn("w-4 h-4 mr-2", refreshing && "animate-spin")} />
                Refresh
              </Button>

              {/* Export Options */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="bg-white" disabled={loading}>
                    <Download className="w-4 h-4 mr-2" />
                    Export
                    {loading && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current ml-2"></div>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Export Options</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleExportPDF}>
                    <FileText className="w-4 h-4 mr-2" />
                    PDF Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportExcel}>
                    <FileText className="w-4 h-4 mr-2" />
                    Excel Report
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportDetailedReport}>
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Detailed Analysis
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSendEmail}>
                    <Mail className="w-4 h-4 mr-2" />
                    Send via Email
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Semester Tabs */}
              <div className="flex bg-white rounded-lg p-1 shadow-sm border">
                <button
                  onClick={() => setTab("Q2")}
                  className={cn(
                    "px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center space-x-2",
                    tab === "Q2"
                      ? "bg-blue-600 text-white shadow-sm"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50",
                  )}
                >
                  <Calendar className="w-4 h-4" />
                  <span>Semester 1 (Q2)</span>
                </button>
                <button
                  onClick={() => setTab("Q4")}
                  className={cn(
                    "px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center space-x-2",
                    tab === "Q4"
                      ? "bg-blue-600 text-white shadow-sm"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50",
                  )}
                >
                  <Calendar className="w-4 h-4" />
                  <span>Semester 2 (Q4)</span>
                </button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  },
)

const MetricsAnalytics = observer(() => {
  const { store } = useAppContext()
  const department = store.auth.department
  const measures = store.measure.all
  const objectives = store.objective.all
  const users = store.user.departmentalUsers

  const get_userId = useMemo(() => {
    let _users: User[] = []
    _users = [...users.filter((u) => u.asJson.department === department)]
    const user_id = _users.map((u) => u.asJson.uid)
    return user_id
  }, [users, department])

  const _measures = useMemo(() => {
    const _measuresArray: Measure[] = []
    get_userId.map((uid) => {
      for (const measure of measures) {
        if (measure.asJson.uid === uid) {
          _measuresArray.push(measure)
        }
      }
    })
    return _measuresArray
  }, [measures, get_userId])

  const _objectives = useMemo(() => {
    const _objectivesArray: Objective[] = []
    get_userId.map((uid) => {
      for (const objective of objectives) {
        if (objective.asJson.uid === uid) {
          _objectivesArray.push(objective)
        }
      }
    })
    return _objectivesArray
  }, [objectives, get_userId])

  const red_measures = _measures.filter((measure) => Number(measure.asJson.finalRating2 || 0) <= 2)
  const good_measures = _measures.filter((measure) => Number(measure.asJson.finalRating2 || 0) >= 3)

  const $measures = _measures.map((measure) => measure.asJson)
  const rating = totalQ2MeasureRating($measures)
  const rating2 = totalQ4MeasureRating($measures)

const StatCard = ({
  value,
  label,
  icon,
  colorClass,
}: {
  value: string | number
  label: string
  icon: React.ReactNode
  colorClass: string
}) => (
  <Card className="flex items-center p-4 shadow-sm hover:shadow-md transition-shadow duration-200">
    <div className={cn("p-3 rounded-full mr-4", colorClass)}>{icon}</div>
    <div>
      <p className="text-2xl font-bold text-gray-900">
        {typeof value === "number"
          ? isNaN(value)
            ? "0.00"
            : value.toFixed(2)
          : value}
      </p>
      <p className="text-sm text-gray-600">{label}</p>
    </div>
  </Card>
)


  return (
    <div className="space-y-6">
      {/* Main Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          value={_measures.length}
          label="All KPIs"
          icon={<Target className="w-6 h-6 text-white" />}
          colorClass="bg-blue-500"
        />
        <StatCard
          value={red_measures.length}
          label="Red KPIs"
          icon={<XCircle className="w-6 h-6 text-white" />}
          colorClass="bg-red-500"
        />
        <StatCard
          value={good_measures.length}
          label="Good KPIs"
          icon={<CheckCircle2 className="w-6 h-6 text-white" />}
          colorClass="bg-green-500"
        />
        <StatCard
          value={_objectives.length}
          label="Objectives"
          icon={<Target className="w-6 h-6 text-white" />}
          colorClass="bg-purple-500"
        />
      </div>

      {/* Quarterly Scores */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            Quarterly Scores
          </CardTitle>
        </CardHeader>


        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard
              value={rating.toFixed(2)}
              label="Midterm Score"
              icon={<TrendingUp className="w-6 h-6 text-white" />}
              colorClass={rating <= 2 ? "bg-yellow-500" : "bg-blue-500"}
            />
            <StatCard
              value={rating2.toFixed(2)}
              label="Final Score"
              icon={<CheckCircle2 className="w-6 h-6 text-white" />}
              colorClass={rating2 <= 2 ? "bg-yellow-500" : "bg-green-500"}
            />
            <StatCard
              value={users.length}
              label="People"
              icon={<Users className="w-6 h-6 text-white" />}
              colorClass="bg-indigo-500"
            />
          </div>
        </CardContent>

      </Card>
    </div>
  )
})

const RatingAnalytics = observer(() => {
  const { store } = useAppContext()
  const department = store.auth.department
  const measures = store.measure.all
  const users = store.user.departmentalUsers

  const get_userId = useMemo(() => {
    let _users: User[] = []
    _users = [...users.filter((u) => u.asJson.department === department)]
    const user_id = _users.map((u) => u.asJson.uid)
    return user_id
  }, [users, department])

  const _measures = useMemo(() => {
    const _measuresArray: Measure[] = []
    get_userId.map((uid) => {
      for (const measure of measures) {
        if (measure.asJson.uid === uid) {
          _measuresArray.push(measure)
        }
      }
    })
    return _measuresArray
  }, [measures, get_userId])

  const labels = ["Rating 1", "Rating 2", "Rating 3", "Rating 4", "Rating 5"]

  const q4a_rating = _measures.reduce(
    (acc, val) => {
      const rate = val.asJson.autoRating2 || 0
      const rating = Math.floor(rate)
      const pos = rating - 1
      acc[pos] = acc[pos] + 1
      return acc
    },
    [0, 0, 0, 0, 0],
  )

  const q4s_rating = _measures.reduce(
    (acc, val) => {
      const rate = val.asJson.supervisorRating2 || 0
      const rating = Math.floor(rate)
      const pos = rating - 1
      acc[pos] = acc[pos] + 1
      return acc
    },
    [0, 0, 0, 0, 0],
  )

  const q4f_rating = _measures.reduce(
    (acc, val) => {
      const rate = val.asJson.finalRating2 || 0
      const rating = Math.floor(rate)
      const pos = rating - 1
      acc[pos] = acc[pos] + 1
      return acc
    },
    [0, 0, 0, 0, 0],
  )

  const ChartCard = ({ title, data }: { title: string; data: number[] }) => (
    <Card className="shadow-sm">
      <CardHeader>
        <CardTitle className="text-base font-semibold text-gray-900">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-80">
          <BarGraph
            title=""
            ylabel="Measures"
            labels={labels}
            data={data}
            scales={{ y: { min: 0, max: _measures.length } }}
          />
        </div>
      </CardContent>
    </Card>
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="w-5 h-5 text-blue-600" />
        <h2 className="text-lg font-semibold text-gray-900">Rating Distribution Analysis</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ChartCard title="Employee Self-Rating" data={q4a_rating} />
        <ChartCard title="Supervisor Rating" data={q4s_rating} />
        <ChartCard title="Final Rating" data={q4f_rating} />
      </div>
    </div>
  )
})

// Add these new chart components after RatingAnalytics

const PerformanceTrendsChart = observer(() => {
  const { store } = useAppContext()
  const department = store.auth.department
  const measures = store.measure.all
  const users = store.user.departmentalUsers

  const get_userId = useMemo(() => {
    let _users: User[] = []
    _users = [...users.filter((u) => u.asJson.department === department)]
    return _users.map((u) => u.asJson.uid)
  }, [users, department])

  const _measures = useMemo(() => {
    const _measuresArray: Measure[] = []
    get_userId.map((uid) => {
      for (const measure of measures) {
        if (measure.asJson.uid === uid) {
          _measuresArray.push(measure)
        }
      }
    })
    return _measuresArray
  }, [measures, get_userId])

  // Calculate quarterly performance trends
  const quarterlyData = useMemo(() => {
    const quarters = ["Q1", "Q2", "Q3", "Q4"]
    const performanceData = quarters.map((quarter, index) => {
      const quarterActuals = _measures.map((measure) => {
        const actual = (measure.asJson[`quarter${index + 1}Actual` as keyof typeof measure.asJson] as number) || 0
        const target = measure.asJson.annualTarget ? measure.asJson.annualTarget / 4 : 0
        return target > 0 ? (actual / target) * 100 : 0
      })

      const avgPerformance =
        quarterActuals.length > 0 ? quarterActuals.reduce((sum, perf) => sum + perf, 0) / quarterActuals.length : 0

      return Math.min(avgPerformance, 150) // Cap at 150% for better visualization
    })

    return {
      labels: quarters,
      datasets: [
        {
          label: "Performance %",
          data: performanceData,
          borderColor: "rgb(59, 130, 246)",
          backgroundColor: "rgba(59, 130, 246, 0.1)",
          tension: 0.4,
          fill: true,
        },
      ],
    }
  }, [_measures])

  return (
    <Card className="shadow-sm">
      <CardHeader>
        <CardTitle className="text-base font-semibold text-gray-900 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-blue-600" />
          Quarterly Performance Trends
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-80">
          <canvas
            ref={(canvas) => {
              if (canvas) {
                const ctx = canvas.getContext("2d")
                if (ctx) {
                  // Simple line chart implementation
                  const data = quarterlyData.datasets[0].data
                  const max = Math.max(...data, 100)
                  const width = canvas.width
                  const height = canvas.height

                  ctx.clearRect(0, 0, width, height)
                  ctx.strokeStyle = "rgb(59, 130, 246)"
                  ctx.lineWidth = 3
                  ctx.beginPath()

                  data.forEach((value, index) => {
                    const x = (index / (data.length - 1)) * (width - 40) + 20
                    const y = height - 40 - (value / max) * (height - 80)

                    if (index === 0) ctx.moveTo(x, y)
                    else ctx.lineTo(x, y)
                  })

                  ctx.stroke()

                  // Draw points
                  ctx.fillStyle = "rgb(59, 130, 246)"
                  data.forEach((value, index) => {
                    const x = (index / (data.length - 1)) * (width - 40) + 20
                    const y = height - 40 - (value / max) * (height - 80)
                    ctx.beginPath()
                    ctx.arc(x, y, 4, 0, 2 * Math.PI)
                    ctx.fill()
                  })
                }
              }
            }}
            width={400}
            height={300}
            className="w-full h-full"
          />
        </div>
      </CardContent>
    </Card>
  )
})

const PerspectivePerformanceChart = observer(() => {
  const { store } = useAppContext()
  const department = store.auth.department
  const measures = store.measure.all
  const users = store.user.departmentalUsers

  const get_userId = useMemo(() => {
    let _users: User[] = []
    _users = [...users.filter((u) => u.asJson.department === department)]
    return _users.map((u) => u.asJson.uid)
  }, [users, department])

  const _measures = useMemo(() => {
    const _measuresArray: Measure[] = []
    get_userId.map((uid) => {
      for (const measure of measures) {
        if (measure.asJson.uid === uid) {
          _measuresArray.push(measure)
        }
      }
    })
    return _measuresArray
  }, [measures, get_userId])

  const perspectiveData = useMemo(() => {
    const perspectives = ["Financial", "Customer", "Internal Process", "Learning & Growth"]
    const data = perspectives.map((perspective) => {
      const perspectiveMeasures = _measures.filter(
        (m) =>
          m.asJson.perspective === perspective ||
          (perspective === "Internal Process" && m.asJson.perspective === "Operational"),
      )

      if (perspectiveMeasures.length === 0) return 0

      const avgRating =
        perspectiveMeasures.reduce((sum, measure) => sum + (measure.asJson.finalRating2 || 0), 0) /
        perspectiveMeasures.length

      return avgRating
    })

    return { labels: perspectives, data }
  }, [_measures])

  return (
    <Card className="shadow-sm">
      <CardHeader>
        <CardTitle className="text-base font-semibold text-gray-900 flex items-center gap-2">
          <Target className="w-5 h-5 text-green-600" />
          Performance by Perspective
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {perspectiveData.labels.map((label, index) => {
            const value = perspectiveData.data[index]
            const percentage = (value / 5) * 100
            const colorClass = value >= 3 ? "bg-green-500" : value >= 2 ? "bg-yellow-500" : "bg-red-500"

            return (
              <div key={label} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">{label}</span>
                  <span className="text-sm font-bold text-gray-900">{value.toFixed(1)}/5.0</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all duration-300 ${colorClass}`}
                    style={{ width: `${Math.min(percentage, 100)}%` }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
})

const EmployeePerformanceChart = observer(() => {
  const { store } = useAppContext()
  const department = store.auth.department
  const measures = store.measure.all
  const users = store.user.departmentalUsers

  const employeePerformance = useMemo(() => {
    const departmentUsers = users.filter((u) => u.asJson.department === department)

    return departmentUsers
      .map((user) => {
        const userMeasures = measures.filter((m) => m.asJson.uid === user.asJson.uid)
        const avgRating =
          userMeasures.length > 0
            ? userMeasures.reduce((sum, measure) => sum + (measure.asJson.finalRating2 || 0), 0) / userMeasures.length
            : 0

        return {
          name: user.asJson.displayName || "Unknown",
          rating: avgRating,
          measuresCount: userMeasures.length,
        }
      })
      .sort((a, b) => b.rating - a.rating)
  }, [users, measures, department])

  return (
    <Card className="shadow-sm">
      <CardHeader>
        <CardTitle className="text-base font-semibold text-gray-900 flex items-center gap-2">
          <Users className="w-5 h-5 text-purple-600" />
          Employee Performance Ranking
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 max-h-80 overflow-y-auto">
          {employeePerformance.map((employee, index) => {
            const isTopPerformer = index < 3
            const isBottomPerformer = index >= employeePerformance.length - 3 && employeePerformance.length > 3

            return (
              <div
                key={employee.name}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  isTopPerformer
                    ? "bg-green-50 border-green-200"
                    : isBottomPerformer
                      ? "bg-red-50 border-red-200"
                      : "bg-gray-50 border-gray-200"
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      isTopPerformer
                        ? "bg-green-500 text-white"
                        : isBottomPerformer
                          ? "bg-red-500 text-white"
                          : "bg-gray-500 text-white"
                    }`}
                  >
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{employee.name}</p>
                    <p className="text-xs text-gray-500">{employee.measuresCount} KPIs</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-gray-900">{employee.rating.toFixed(1)}</p>
                  <div className="flex items-center space-x-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <div
                        key={star}
                        className={`w-3 h-3 rounded-full ${star <= employee.rating ? "bg-yellow-400" : "bg-gray-200"}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
})

const KPIAchievementChart = observer(() => {
  const { store } = useAppContext()
  const department = store.auth.department
  const measures = store.measure.all
  const users = store.user.departmentalUsers

  const get_userId = useMemo(() => {
    let _users: User[] = []
    _users = [...users.filter((u) => u.asJson.department === department)]
    return _users.map((u) => u.asJson.uid)
  }, [users, department])

  const _measures = useMemo(() => {
    const _measuresArray: Measure[] = []
    get_userId.map((uid) => {
      for (const measure of measures) {
        if (measure.asJson.uid === uid) {
          _measuresArray.push(measure)
        }
      }
    })
    return _measuresArray
  }, [measures, get_userId])

  const achievementData = useMemo(() => {
    const total = _measures.length
    const excellent = _measures.filter((m) => (m.asJson.finalRating2 || 0) >= 4).length
    const good = _measures.filter((m) => (m.asJson.finalRating2 || 0) >= 3 && (m.asJson.finalRating2 || 0) < 4).length
    const satisfactory = _measures.filter(
      (m) => (m.asJson.finalRating2 || 0) >= 2 && (m.asJson.finalRating2 || 0) < 3,
    ).length
    const poor = _measures.filter((m) => (m.asJson.finalRating2 || 0) < 2).length

    return [
      {
        label: "Excellent (4-5)",
        value: excellent,
        percentage: total > 0 ? (excellent / total) * 100 : 0,
        color: "bg-green-500",
      },
      { label: "Good (3-4)", value: good, percentage: total > 0 ? (good / total) * 100 : 0, color: "bg-blue-500" },
      {
        label: "Satisfactory (2-3)",
        value: satisfactory,
        percentage: total > 0 ? (satisfactory / total) * 100 : 0,
        color: "bg-yellow-500",
      },
      { label: "Poor (<2)", value: poor, percentage: total > 0 ? (poor / total) * 100 : 0, color: "bg-red-500" },
    ]
  }, [_measures])

  return (
    <Card className="shadow-sm">
      <CardHeader>
        <CardTitle className="text-base font-semibold text-gray-900 flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-green-600" />
          KPI Achievement Distribution
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {achievementData.map((item, index) => (
            <div key={index} className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">{item.label}</span>
                <div className="text-right">
                  <span className="text-sm font-bold text-gray-900">{item.value}</span>
                  <span className="text-xs text-gray-500 ml-1">({item.percentage.toFixed(1)}%)</span>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all duration-300 ${item.color}`}
                  style={{ width: `${item.percentage}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
})

const CompletionRateChart = observer(() => {
  const { store } = useAppContext()
  const department = store.auth.department
  const measures = store.measure.all
  const objectives = store.objective.all
  const users = store.user.departmentalUsers

  const get_userId = useMemo(() => {
    let _users: User[] = []
    _users = [...users.filter((u) => u.asJson.department === department)]
    return _users.map((u) => u.asJson.uid)
  }, [users, department])

  const completionData = useMemo(() => {
    const departmentMeasures = measures.filter((m) => get_userId.includes(m.asJson.uid))
    const departmentObjectives = objectives.filter((o) => get_userId.includes(o.asJson.uid))

    const measuresWithRatings = departmentMeasures.filter((m) => m.asJson.finalRating2 !== null).length
    const objectivesWithComments = departmentObjectives.filter((o) => o.asJson.assessComment).length

    return [
      {
        category: "KPIs Completed",
        completed: measuresWithRatings,
        total: departmentMeasures.length,
        percentage: departmentMeasures.length > 0 ? (measuresWithRatings / departmentMeasures.length) * 100 : 0,
      },
      {
        category: "Objectives Reviewed",
        completed: objectivesWithComments,
        total: departmentObjectives.length,
        percentage: departmentObjectives.length > 0 ? (objectivesWithComments / departmentObjectives.length) * 100 : 0,
      },
    ]
  }, [measures, objectives, get_userId])

  return (
    <Card className="shadow-sm">
      <CardHeader>
        <CardTitle className="text-base font-semibold text-gray-900 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-indigo-600" />
          Completion Rate Analysis
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {completionData.map((item, index) => (
            <div key={index} className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">{item.category}</span>
                <span className="text-sm font-bold text-gray-900">
                  {item.completed}/{item.total} ({item.percentage.toFixed(1)}%)
                </span>
              </div>
              <div className="relative">
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div
                    className={`h-4 rounded-full transition-all duration-300 ${
                      item.percentage >= 80 ? "bg-green-500" : item.percentage >= 60 ? "bg-yellow-500" : "bg-red-500"
                    }`}
                    style={{ width: `${item.percentage}%` }}
                  />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xs font-medium text-white drop-shadow">{item.percentage.toFixed(0)}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
})

const DepartmentDashboard = observer(() => {
  const { api, store, ui } = useAppContext()
  const [loading, setLoading] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)
  const [tab, setTab] = useState<"Q2" | "Q4">("Q2")
  const chartRef = useRef<HTMLDivElement>(null)
  const department = store.auth.department || ""
  const departmentName = department.charAt(0).toUpperCase() + department.slice(1)

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        await api.measure.getAll()
        await api.user.getByDepartment(department)
        await api.objective.getAll()
      } catch (error) {
        console.error("Error loading department data:", error)
        ui.snackbar.load({
          id: Date.now(),
          message: "Error loading department data",
          type: "danger",
        })
      }
      setLoading(false)
    }
    load()
  }, [api.measure, api.user, api.objective, department])

  const handleExportPDF = async () => {
    if (chartRef.current === null) return
    try {
      setExportLoading(true)
      await toPng(chartRef.current, { cacheBust: true }).then(async (dataUrl) => {
        const link = document.createElement("a")
        link.href = dataUrl
        link.download = `${departmentName}_Department_Dashboard.png`
        link.click()
      })
      ui.snackbar.load({
        id: Date.now(),
        message: "Dashboard exported successfully!",
        type: "success",
      })
    } catch (error) {
      ui.snackbar.load({
        id: Date.now(),
        message: "Error! Failed to export dashboard.",
        type: "danger",
      })
    }
    setExportLoading(false)
  }

  const handleExportExcel = async () => {
    setExportLoading(true)
    try {
      // Simulate Excel export
      await new Promise((resolve) => setTimeout(resolve, 2000))
      ui.snackbar.load({
        id: Date.now(),
        message: "Excel report exported successfully!",
        type: "success",
      })
    } catch (error) {
      ui.snackbar.load({
        id: Date.now(),
        message: "Error! Failed to export Excel.",
        type: "danger",
      })
    }
    setExportLoading(false)
  }

  const handleExportDetailedReport = async () => {
    setExportLoading(true)
    try {
      // Simulate detailed report generation
      await new Promise((resolve) => setTimeout(resolve, 3000))
      ui.snackbar.load({
        id: Date.now(),
        message: "Detailed analysis report generated successfully!",
        type: "success",
      })
    } catch (error) {
      ui.snackbar.load({
        id: Date.now(),
        message: "Error! Failed to generate detailed report.",
        type: "danger",
      })
    }
    setExportLoading(false)
  }

  const handleSendEmail = async () => {
    setExportLoading(true)
    try {
      // Simulate email sending
      await new Promise((resolve) => setTimeout(resolve, 2000))
      ui.snackbar.load({
        id: Date.now(),
        message: "Dashboard sent successfully via email!",
        type: "success",
      })
    } catch (error) {
      ui.snackbar.load({
        id: Date.now(),
        message: "Error! Failed to send email.",
        type: "danger",
      })
    }
    setExportLoading(false)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 p-4 sm:p-6 w-full flex items-center justify-center">
        <LoadingEllipsis />
      </div>
    )
  }

  // Update the return statement in DepartmentDashboard component
  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 p-4 sm:p-6 w-full">
        <div className="space-y-6">
          <DepartmentTabs
            tab={tab}
            setTab={setTab}
            handleExportPDF={handleExportPDF}
            handleExportExcel={handleExportExcel}
            handleExportDetailedReport={handleExportDetailedReport}
            handleSendEmail={handleSendEmail}
            loading={exportLoading}
            departmentName={departmentName}
          />

          <div ref={chartRef} className="space-y-6">
            <MetricsAnalytics />

            {/* Performance Overview Charts */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <PerformanceTrendsChart />
              <PerspectivePerformanceChart />
            </div>

            {/* Employee and Achievement Analysis */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <EmployeePerformanceChart />
              <KPIAchievementChart />
            </div>

            {/* Completion and Rating Analysis */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <CompletionRateChart />
              <div className="space-y-6">
                <RatingAnalytics />
              </div>
            </div>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  )
})

export default DepartmentDashboard
